import { PopupContainerProvider } from '@/components/ReportForm/Container/PopupContainer';
import {
  HeaderSection,
  SingleRow,
  SingleRowWithCopy,
} from '@/features/ap_payees/components/PayeeDetailDrawer/components/Common';
import { SingleRowProps } from '@/features/ap_payees/components/PayeeDetailDrawer/components/type';
import { usePayeeDetailDrawer } from '@/features/ap_payees/components/PayeeDetailDrawer/context/PayeeDetailDrawerProvider';
import {
  EmailReceivingMethodData,
  InvoiceReceiveEmailProcessType,
  SECTION_KEYS,
} from '@/features/ap_payees/components/PayeeDetailDrawer/types';
import { useTranslation } from '@/i18n';
import { ButtonV2 } from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, useCallback, useMemo } from 'react';
import parentStyles from '../../../../styles.module.scss';
import { Edit } from './Edit';
import localStyles from './styles.module.scss';
import { useEmailAddressRowConfig } from './useEmailAddressRowConfig';

const cx = classnames.bind({ ...parentStyles, ...localStyles });

type Props = {
  data?: EmailReceivingMethodData;
  apPayeeId: string;
};

type RowData = {
  label: string;
  value: string | undefined;
  ComponentRender: FC<SingleRowProps>;
  helperLabel?: string;
  contentClassName?: string;
};

export const EmailReceivingMethod: FC<Props> = ({ data, apPayeeId }) => {
  const { t } = useTranslation();
  const { isEditingSection, setIsEditingSection } = usePayeeDetailDrawer();
  const isEditing = isEditingSection === SECTION_KEYS.EMAIL;

  const {
    is_invoice_receive_email_setting_existed,
    invoice_receive_email_address,
    invoice_receive_email_process_type,
    contact_office_member,
  } = data || {};

  const emailAddressRowConfig = useEmailAddressRowConfig({ data });

  const onActionButtonClick = useCallback(
    (e?: React.MouseEvent) => {
      e?.preventDefault();
      e?.stopPropagation();
      setIsEditingSection(SECTION_KEYS.EMAIL);
    },
    [setIsEditingSection]
  );

  const rowRenderData = useMemo(() => {
    const rowData: RowData[] = [];
    // ------------------------------------------------------------
    // After auto-import email feature release
    // ------------------------------------------------------------

    if (!is_invoice_receive_email_setting_existed) {
      rowData.push({
        label: t('ap_payee_detail_drawer_email_receiving_method_location'),
        value: t('received_invoice'),
        ComponentRender: SingleRow,
      });
      rowData.push({
        label: t('ap_payee_detail_drawer_email_receiving_method_address'),
        value: invoice_receive_email_address,
        ComponentRender: SingleRowWithCopy,
      });
      return rowData;
    }

    // ------------------------------------------------------------
    // Before auto-import email feature release
    // ------------------------------------------------------------

    // Process type: Received invoice
    if (
      invoice_receive_email_process_type ===
      InvoiceReceiveEmailProcessType.RECEIVED_INVOICE
    ) {
      rowData.push({
        label: t('ap_payee_detail_drawer_email_receiving_method_location'),
        value: t('received_invoice'),
        ComponentRender: SingleRow,
      });
    }

    // Process type: Payment request
    if (
      invoice_receive_email_process_type ===
      InvoiceReceiveEmailProcessType.PAYMENT_REQUEST
    ) {
      rowData.push({
        label: t('ap_payee_detail_drawer_email_receiving_method_location'),
        value: t('ap_payee_mail_receive_payment_request_draft'),
        ComponentRender: SingleRow,
      });
      rowData.push({
        label: t('ap_payee_mail_receive_contact_office_member_name'),
        value: contact_office_member ? contact_office_member?.display_name : '',
        ComponentRender: SingleRow,
        helperLabel: t(
          'ap_payee_detail_drawer_email_receiving_method_contact_office_member_name_tooltip'
        ),
      });
    }

    // Add address row using shared config
    if (emailAddressRowConfig) {
      rowData.push({
        label: t('ap_payee_detail_drawer_email_receiving_method_address'),
        value: invoice_receive_email_address,
        ComponentRender: emailAddressRowConfig.shouldShowCopyButton
          ? SingleRowWithCopy
          : SingleRow,
        contentClassName: emailAddressRowConfig.isSecondaryText
          ? cx('secondaryText')
          : undefined,
      });
    }

    return rowData;
  }, [
    is_invoice_receive_email_setting_existed,
    invoice_receive_email_address,
    invoice_receive_email_process_type,
    contact_office_member,
    emailAddressRowConfig,
    t,
  ]);

  const canEdit = is_invoice_receive_email_setting_existed;

  if (isEditing) {
    return (
      <PopupContainerProvider>
        <div className={cx('subSectionContainer')}>
          <Edit data={data} apPayeeId={apPayeeId} />
        </div>
      </PopupContainerProvider>
    );
  }

  return (
    <div className={cx('subSectionContainer')}>
      <HeaderSection
        displayName={t('ap_payee_mail_receive')}
        isSubHeader
        actionButton={
          canEdit ? (
            <ButtonV2
              onClick={onActionButtonClick}
              isSecondary
              isDisabled={isEditingSection !== SECTION_KEYS.NULL}
            >
              {t('edit')}
            </ButtonV2>
          ) : undefined
        }
      />
      <div className={cx('bodySectionContainer')}>
        {rowRenderData.map((item, index) => (
          <item.ComponentRender
            key={`${item.label}-${index}`}
            label={item.label}
            value={item.value ?? ''}
            helperLabel={item?.helperLabel}
            contentClassName={item?.contentClassName}
          />
        ))}
      </div>
    </div>
  );
};

EmailReceivingMethod.displayName = 'EmailReceivingMethod';
